import { useEffect, useMemo, useState } from 'react'
import type { RootUser as User } from '@einwert/valuation-api-client'
import { initI18n, i18n } from 'ClientPortal/src/i18n/init'
import { DEFAULT_LOCALE, getBrowserLocale, resolveLocale } from 'ClientPortal/src/i18n/supportedLocales'
import { useFetchFeatureFlags } from 'ClientPortal/src/api/Queries/feature-flags'

export function useResolvedLocale({ user, isUserPending }: { user?: User; isUserPending: boolean }) {
    const { featureFlags, isFeatureFlagsPending } = useFetchFeatureFlags({})
    const [isReady, setIsReady] = useState(i18n.isInitialized)
    const [error, setError] = useState<unknown>()
    const isLocaleDataPending = isUserPending || isFeatureFlagsPending
    const resolvedLocale = useMemo(
        () =>
            featureFlags?.enable_localization
                ? resolveLocale({
                      userLocale: user?.locale,
                      browserLocale: getBrowserLocale(),
                  })
                : DEFAULT_LOCALE,
        [featureFlags?.enable_localization, user?.locale]
    )

    useEffect(() => {
        if (isLocaleDataPending) {
            return
        }

        let isMounted = true

        void initI18n(resolvedLocale)
            .then(() => {
                if (isMounted) {
                    setError(undefined)
                    setIsReady(true)
                }
            })
            .catch((initError) => {
                if (isMounted) {
                    setError(initError)
                }
            })

        return () => {
            isMounted = false
        }
    }, [isLocaleDataPending, resolvedLocale])

    return {
        error,
        isReady: isReady && !isLocaleDataPending,
        resolvedLocale,
    }
}
